/**
 * English strings for the submissions screens.
 *
 * Written out in full and derived from nothing — see en/index.ts. Typed as
 * a complete slice, so a key missing here is a build error rather than a
 * screen that quietly renders Bosnian.
 *
 * *Prijava* is "report" throughout, matching the sign-in screen: the thing a
 * worker files about a machine. "Submission" is what the code calls it and
 * nobody on the floor would.
 */

import type { SubmissionsSlice } from '../bs/submissions'

export const submissions: SubmissionsSlice = {
  // ---- Shared by more than one screen ------------------------------
  'submissions.overdue': 'Overdue',
  'submissions.cancel': 'Cancel',

  'submissions.status.chain.hint':
    '“{new}” is waiting for somebody to pick it up, “{inProgress}” means somebody is working on it, “{resolved}” that the job is done and waiting to be signed off, and “{closed}” that the report is finished and changes no further — a supervisor moves it on, not the person who reported it.',

  // ---- The list ----------------------------------------------------
  'submissions.list.title': 'Reports',
  'submissions.list.subtitle.all': 'Every report in the plant',
  'submissions.list.subtitle.mine': 'Your reports',
  'submissions.list.filters': 'Filters',
  'submissions.list.search.placeholder': 'Search by number, title or description',
  'submissions.list.search.label': 'Search reports',

  'submissions.list.filter.status': 'Status',
  'submissions.list.filter.status.all': 'All statuses',
  'submissions.list.filter.type': 'Type',
  'submissions.list.filter.type.all': 'All types',
  'submissions.list.filter.workCenter': 'Work centre',
  'submissions.list.filter.workCenter.all': 'All work centres',
  'submissions.list.sort': 'Sorting',
  'submissions.list.sort.newest': 'Newest first',
  'submissions.list.sort.oldest': 'Oldest first',
  'submissions.list.sort.priority': 'By priority',
  'submissions.list.sort.due': 'By due date',

  'submissions.list.filter.openOnly': 'Open only',
  'submissions.list.filter.overdue': 'Overdue only',
  'submissions.list.filter.unassigned': 'Nobody assigned',
  'submissions.list.filter.assignedToMe': 'Assigned to me',

  'submissions.list.empty.title': 'No reports',
  'submissions.list.empty.description': 'Change the filters, or make a new report.',

  'submissions.list.previous': 'Previous',
  'submissions.list.next': 'Next',

  // ---- One submission ----------------------------------------------
  'submissions.detail.back': 'Back to reports',
  'submissions.detail.print': 'Print',
  'submissions.detail.detailedReport': 'Full report',
  'submissions.detail.delete': 'Delete',

  'submissions.detail.field.type': 'Type',
  'submissions.detail.field.due': 'Due',
  'submissions.detail.field.assignee': 'Assigned to',
  'submissions.detail.field.resolvedIn': 'Resolved in',

  'submissions.detail.field.due.hint':
    'The due date is worked out from the priority at the moment the report was filed — changing the priority afterwards does not move it.',
  'submissions.detail.field.resolvedIn.hint':
    'The hours from the moment the report was sent to the moment somebody marked it resolved — plain elapsed time, not working hours.',

  'submissions.detail.attachments': 'Attachments',
  'submissions.detail.attachment.failed': 'That attachment could not be opened.',

  'submissions.detail.work': 'Handling the report',
  'submissions.detail.assign.label': 'Assigned to',
  'submissions.detail.assign.hint':
    'Assigning a new report moves it on to the next status at the same time.',
  'submissions.detail.assign.who':
    'Reports are assigned by the responsible person, the committee and administrators; maintenance works on them but cannot hand one on to somebody else.',
  'submissions.detail.assign.none': 'Nobody',
  'submissions.detail.assign.done': 'The report has been assigned.',
  'submissions.detail.assign.cleared': 'The assignment has been removed.',
  'submissions.detail.assign.failed': 'Assigning did not work.',

  'submissions.detail.status.label': 'New status',
  'submissions.detail.status.hint':
    'Only the statuses this report is allowed to move to from where it is now are offered.',
  'submissions.detail.status.commit':
    'Only a supervisor changes the status, every change is written into the history with your name and the time, and the person who filed the report is told.',
  'submissions.detail.status.choose': 'Choose',
  'submissions.detail.status.note': 'Note',
  'submissions.detail.status.note.placeholder': 'Optional — why you are changing the status.',
  'submissions.detail.status.save': 'Save status',
  'submissions.detail.status.changed': 'The status has been changed.',
  'submissions.detail.status.failed': 'The change did not go through.',
  'submissions.detail.status.locked': 'The report is at “{status}” and moves no further.',

  'submissions.detail.history': 'History',
  'submissions.detail.history.created': 'Created',
  'submissions.detail.history.duration':
    'The time beside each entry is how long the report sat in the status before it, up to that change.',

  'submissions.detail.delete.title': 'Delete report {reference}?',
  'submissions.detail.delete.irreversible': 'This cannot be undone.',
  'submissions.detail.delete.intro': 'The report goes, and everything that came with it:',
  /*
   * English needs two forms where Bosnian needs three, so "few" and "many"
   * carry the same sentence. Keeping all three keys is what lets one call in
   * the component serve every language.
   */
  'submissions.detail.delete.attachments.one':
    '{count} attachment — the file is deleted from the server as well',
  'submissions.detail.delete.attachments.few':
    '{count} attachments — the files are deleted from the server as well',
  'submissions.detail.delete.attachments.many':
    '{count} attachments — the files are deleted from the server as well',
  'submissions.detail.delete.tasks.one': '{count} task',
  'submissions.detail.delete.tasks.few': '{count} tasks',
  'submissions.detail.delete.tasks.many': '{count} tasks',
  'submissions.detail.delete.reviews.one': '{count} assessment',
  'submissions.detail.delete.reviews.few': '{count} assessments',
  'submissions.detail.delete.reviews.many': '{count} assessments',
  'submissions.detail.delete.history': 'the whole status history',
  'submissions.detail.delete.audit': 'Who deleted it, and when, stays in the activity log.',
  'submissions.detail.delete.confirm': 'Delete for good',
  'submissions.detail.delete.failed': 'The delete did not go through.',

  // ---- The improvement block, read and written ---------------------
  'submissions.improvement.currentState': 'Current situation',
  'submissions.improvement.proposal': 'Proposal',
  'submissions.improvement.expectedImpact': 'Expected effect',
  'submissions.improvement.duration': 'Duration',
  'submissions.improvement.estimatedSavings': 'Estimated saving',
  'submissions.improvement.estimatedCost': 'Estimated cost',
  'submissions.improvement.actualSavings': 'Actual saving',

  // ---- The form ----------------------------------------------------
  'submissions.form.heading': 'New report',
  'submissions.form.subtitle':
    'You can send just a voice message, a photo or a video — a description is not required.',

  'submissions.form.help':
    'You can fill a report in with no signal at all — it is kept on the phone and sends itself once the connection is back, so you can close the app. Every report that has been sent has a number of its own (e.g. PR-2026-0042) that you can read out or write on a work order.',

  'submissions.form.type': 'Type of report',
  'submissions.form.type.hint':
    'A breakdown, an incident and a safety issue go to a supervisor to be handed to somebody to fix, while an improvement proposal opens extra fields and goes to an assessment that can carry an award.',
  'submissions.form.workCenter': 'Work centre',
  'submissions.form.workCenter.choose': 'Choose a work centre',
  'submissions.form.workCenter.hint':
    'If the machine has a QR sticker, scan it rather than hunting through the list — the work centre is then filled in for you.',
  'submissions.form.priority': 'Priority',
  'submissions.form.priority.hint':
    'Use the highest priority only when production has stopped or somebody could get hurt.',
  'submissions.form.title': 'Title',
  'submissions.form.title.hint': 'Short, e.g. “Belt is slipping”',
  'submissions.form.title.placeholder': 'e.g. Belt is slipping',
  'submissions.form.description': 'Description',
  'submissions.form.description.placeholder':
    'What is happening, since when, and what you have tried so far.',

  'submissions.form.improvement.heading': 'Proposal details',
  'submissions.form.improvement.currentState.placeholder':
    'How the job is done today, and what the problem is.',
  'submissions.form.improvement.proposal.placeholder': 'What exactly to change.',
  'submissions.form.improvement.expectedImpact.placeholder':
    'Time saved, less scrap, a safer place to work…',
  'submissions.form.improvement.duration': 'Time to carry it out',
  'submissions.form.improvement.duration.placeholder': 'e.g. 2 weeks',
  'submissions.form.improvement.possibleDate': 'Possible date',
  'submissions.form.improvement.estimatedSavings': 'Estimated saving ({currency})',
  'submissions.form.improvement.estimatedCost': 'Estimated cost ({currency})',
  'submissions.form.improvement.estimate.hint':
    'This is an estimate — what the saving and the cost actually came to is entered later by a supervisor, once the proposal has been carried out.',

  'submissions.form.voice.hint':
    'Say what is wrong instead of typing it. The message is sent along with the report.',
  'submissions.form.voice.explain':
    'If saying it is easier than typing it, record one message — your supervisor plays it back on the report itself.',
  'submissions.form.media.heading': 'Photos and video',
  'submissions.form.media.add': 'Add a photo or a video',
  'submissions.form.media.remove': 'Remove {name}',
  'submissions.form.media.hint':
    'A photograph is what makes a report fixable — whoever comes out to it sees the fault before they set off and knows what to bring.',

  'submissions.form.submit': 'Send the report',
  'submissions.form.saveToDevice': 'Save to the device',
  'submissions.form.offlineNotice':
    'There is no connection to the server right now. The report is kept on the device and will be sent by itself once the connection is back — you can close the app.',

  'submissions.form.needContent': 'Add a description, a voice message or an attachment.',
  'submissions.form.needContent.toast':
    'Add a description, a voice message or an attachment before saving.',
  'submissions.form.savedOffline':
    'No connection — the report is saved on the device and will send by itself.',
  'submissions.form.savedAfterFailure':
    'Sending failed, but the report is saved and will be sent by itself.',
  'submissions.form.saveFailed': 'Saving to the device did not work.',
  'submissions.form.sent': 'Report {reference} has been sent.',
  'submissions.form.sendFailed': 'Sending failed.',

  // ---- Tasks hanging off a submission ------------------------------
  'submissions.tasks.title': 'Tasks',
  'submissions.tasks.add': 'New task',
  'submissions.tasks.help':
    'When a fix takes several steps, make a task for each one — every task has its own owner and its own deadline, and each is finished on its own. The report itself stays open until you move it on, however many of its tasks are done.',
  'submissions.tasks.empty.manager': 'No tasks. Assign one so that somebody takes the fix on.',
  'submissions.tasks.empty': 'This report has no tasks yet.',
  'submissions.tasks.due': 'due {date}',

  'submissions.tasks.start': 'Start',
  'submissions.tasks.finish': 'Finish',
  'submissions.tasks.verify': 'Verify',

  'submissions.tasks.updated': 'The task has been updated.',
  'submissions.tasks.updateFailed': 'The update did not go through.',
  'submissions.tasks.assigned': 'The task has been assigned.',

  'submissions.tasks.form.title': 'Task',
  'submissions.tasks.form.title.placeholder': 'e.g. Replace the bearing on the planer',
  'submissions.tasks.form.assignee': 'Assigned to',
  'submissions.tasks.form.assignee.choose': 'Choose a person',
  'submissions.tasks.form.description': 'Description',
  'submissions.tasks.form.description.placeholder':
    'Optional — what exactly has to be done, and which parts are needed.',
  'submissions.tasks.form.due': 'Due',
  'submissions.tasks.form.due.hint': 'It cannot be in the past.',
  'submissions.tasks.form.submit': 'Assign the task',

  // ---- The reward scheme -------------------------------------------
  'submissions.reviews.decide.accept': 'Accept',
  'submissions.reviews.decide.pending': 'Hold',
  'submissions.reviews.decide.reject': 'Reject',
  'submissions.reviews.title': 'Assessments',
  'submissions.reviews.add': 'Add an assessment',
  'submissions.reviews.empty.manager':
    'No assessments yet. The responsible manager gives the first one, and the committee makes the final decision and the award.',
  'submissions.reviews.empty': 'This report has not been assessed yet.',

  'submissions.reviews.innovation': 'Innovation',
  'submissions.reviews.materialBenefit': 'Material benefit',
  'submissions.reviews.nonMaterialBenefit': 'Non-material benefit',
  'submissions.reviews.total': 'Total',
  'submissions.reviews.award': 'Award: {amount}',

  'submissions.reviews.decision': 'Decision',
  'submissions.reviews.points.hint':
    'Points are given to improvement proposals only, on three criteria, each 0–5.',
  'submissions.reviews.points.sum': 'Points total',
  'submissions.reviews.points.override': 'Corrected total',
  'submissions.reviews.points.override.hint': 'Leave blank to use the sum.',
  'submissions.reviews.awardField': 'Award ({currency})',
  'submissions.reviews.awardField.hint': 'Only the committee can grant a money award.',
  'submissions.reviews.comment': 'Reasoning',
  'submissions.reviews.comment.placeholder': 'Why the proposal was accepted or turned down.',
  'submissions.reviews.save': 'Save assessment',
  'submissions.reviews.saved': 'The assessment has been saved.',

  // ---- Spoken messages ---------------------------------------------
  'submissions.voice.label': 'Voice message',
  'submissions.voice.record': 'Record a voice message',
  'submissions.voice.stop': 'Stop',
  'submissions.voice.again': 'Record again',
  'submissions.voice.discard': 'Delete the voice message',
  'submissions.voice.unavailable':
    'Sound recording is not available. The app needs a microphone and a secure connection (https).',
  'submissions.voice.empty': 'The recording is empty. Check the microphone and try again.',
  'submissions.voice.play': 'Play {label}',
  'submissions.voice.pause': 'Pause {label}',
  'submissions.voice.download': 'Download {label}',
  'submissions.voice.position': 'Position in the recording — {label}',
  'submissions.voice.unsupported':
    'This browser cannot play this format — download the recording.',
  'submissions.voice.loadFailed': 'The message could not be loaded.',

  // ---- Who was working the centre ----------------------------------
  'submissions.workers.choose': 'Choose the workers',
  'submissions.workers.count.one': '{count} worker',
  'submissions.workers.count.few': '{count} workers',
  'submissions.workers.count.many': '{count} workers',
  'submissions.workers.title': 'Workers at the centre',
  'submissions.workers.clear': 'Clear the choice',
  'submissions.workers.done': 'Done',
  'submissions.workers.hint':
    'Who worked at this centre over the period you are scoring. The work centre is scored, not the worker — this only records who kept it running.',
  'submissions.workers.search.placeholder': 'Name or code',
  'submissions.workers.search.label': 'Search workers',
  'submissions.workers.loading': 'Loading workers…',
  'submissions.workers.unavailable': 'The list is not available',
  'submissions.workers.noResults': 'Nothing found',
  'submissions.workers.noResults.description': 'Change the search.',
  'submissions.workers.narrowed': 'Showing the workers assigned to this work centre.',
  'submissions.workers.showEveryone': 'Show everyone',
  'submissions.attachment.open': 'Open attachment {name}',
  'submissions.edit.title': 'Edit report',
  'submissions.edit.back': 'Back to the report',
  'submissions.edit.saved': 'The report has been updated.',
  'submissions.edit.action': 'Edit',
  'submissions.edit.closed':
    'This report can no longer be changed — somebody has picked it up, or it is closed. Add a comment, or ask a supervisor.',
  'submissions.edit.scoredLocked':
    'The committee has already scored this proposal, so its content no longer changes. The score was given for exactly this text.',
  'submissions.edit.files.hint':
    'Attachments change immediately — the Cancel button below will not bring a deleted file back.',
  'submissions.edit.files.add': 'Add a file',
  'submissions.edit.files.added': 'The file has been added.',
  'submissions.edit.files.remove': 'Delete',
  'submissions.edit.files.removed': 'The file has been deleted.',
  'submissions.edit.files.removeTitle': 'Delete “{name}”?',
  'submissions.edit.files.removeBody':
    'The file is deleted for good, not just taken off the list. This cannot be undone.',
  'submissions.realisation.title': 'Realisation',
  'submissions.realisation.hint':
    'What the proposal actually delivered, when it was carried out and by whom. Filled in after the work — and after the report is closed.',
  'submissions.realisation.by': 'Implemented by',
  'submissions.realisation.byNobody': 'Not recorded',
  'submissions.realisation.date': 'Date implemented',
  'submissions.realisation.dateHint': 'Cannot be in the future.',
  'submissions.realisation.actualCost': 'Actual cost',
  'submissions.realisation.against': 'Estimated: {amount}',
  'submissions.realisation.recorded': 'The realisation has been recorded.',
  'submissions.realisation.saved': 'The realisation has been saved.',

  'submissions.insights.lifecycle.title': 'Where the time went',
  'submissions.insights.lifecycle.hint': 'How long the report spent in each stage.',
  'submissions.insights.lifecycle.series': 'Duration',
  'submissions.insights.lifecycle.current': 'The stage the report is in now.',
  'submissions.insights.benchmark.title': 'Fast or slow?',
  'submissions.insights.benchmark.thisOne': 'This report',
  'submissions.insights.benchmark.usualHere': 'Usual at this work centre',
  'submissions.insights.benchmark.usualType': 'Usual for this kind of report',
  'submissions.insights.benchmark.sample': 'Compared against {count} resolved reports.',
  'submissions.insights.scoring.title': 'How it was scored',
  'submissions.insights.scoring.hint': 'What the committee score is made of.',
  'submissions.insights.scoring.innovation': 'Innovation',
  'submissions.insights.scoring.material': 'Material benefit',
  'submissions.insights.scoring.nonMaterial': 'Non-material benefit',
  'submissions.insights.scoring.points': 'Points',
  'submissions.insights.scoring.total': 'Total: {total} points.',
  'submissions.insights.scoring.median': 'median of accepted proposals: {median}',
  'submissions.insights.realisation.title': 'Promised and delivered',
  'submissions.insights.realisation.hint': 'The estimate from the proposal against what actually happened.',
  'submissions.insights.realisation.savings': 'Saving',
  'submissions.insights.realisation.cost': 'Cost',
  'submissions.insights.realisation.estimated': 'Estimated',
  'submissions.insights.realisation.actual': 'Actual',
  'submissions.insights.recurrence.title': 'Is this recurring?',
  'submissions.insights.recurrence.hint':
    'The same work centre has {count} more reports like this in the last {days} days.',
  'submissions.insights.recurrence.series': 'Reports',

  'submissions.tasks.progress': '{done} of {total} tasks finished.',

}
